import { Variant, Product } from "../../types/types";
import { getDescription } from "../product/getDescription";

type StructuredData = {
  offers: {
    seller: {
      "@type": string;
      name: string;
    };
    priceCurrency: string;
    "@type": string;
    price: number;
    availability: string;
    url: string;
  };
  image: string;
  color: string | null | undefined;
  productId: string;
  "@type": string;
  description: string;
  mpn: string | undefined;
  "@context": string;
  url: string;
  name: string;
  sku: string | undefined;
  brand: {
    "@type": string;
    name: string | undefined;
    logo: string | undefined;
  };
  aggregateRating?: {
    "@type": "AggregateRating";
    reviewCount: number;
    ratingValue: number;
  };
};

export const getStructuredData = (
  product: Product,
  selectedVariant: Variant,
): StructuredData => {
  const {
    __typename: productType,
    title,
    canonicalURL,
    aliases: { pimProductId: productId },
    reviewSummary,
  } = product;
  const description = getDescription(product);
  const canonicalPath = `https://www.johnlewis.com${canonicalURL}`;

  const {
    images: { primary: primaryImage },
  } = selectedVariant;

  let color: string | null | undefined = undefined;
  const { differentiators = {} } = selectedVariant;
  if (differentiators !== null) {
    const { colour } = differentiators;
    color = colour;
  }

  let logo: string | undefined = undefined;
  if (
    productType === "RetiredSimpleProduct" ||
    productType === "RetiredCompositeBundle"
  ) {
    if (product.brand.logo !== undefined) {
      logo = `https:${product.brand.logo}`;
    }
  }

  let mpn: string | undefined = undefined;
  let sku: string | undefined = undefined;
  let brandName: string | undefined = undefined;
  if (
    productType === "RetiredSimpleProduct" ||
    productType === "RetiredCompositeBundle"
  ) {
    mpn = selectedVariant.aliases.skuId;
    sku = selectedVariant.aliases.skuId;
    brandName = product.brand.name;
  } else if (
    productType === "RetiredStaticBundle" &&
    selectedVariant.components
  ) {
    mpn = selectedVariant.components[0]?.variant.aliases.skuId;
    sku = selectedVariant.components[0]?.variant.aliases.skuId;
  }

  let availability = "Discontinued";
  if (selectedVariant.availability?.availableToOrder) {
    availability = "InStock";
  } else if (selectedVariant.availability?.unavailableReason === "outOfStock") {
    availability = "OutOfStock";
  } else if (selectedVariant.availability?.preOrder) {
    availability = "PreOrder";
  } else if (
    selectedVariant.availability?.unavailableReason === "inStoreOnly"
  ) {
    availability = "InStoreOnly";
  }

  const structuredData: StructuredData = {
    offers: {
      seller: {
        "@type": "Organization",
        name: "John Lewis & Partners",
      },
      priceCurrency: "GBP",
      "@type": "Offer",
      price: 0,
      availability: availability,
      url: canonicalPath,
    },
    image: `https:${primaryImage}`,
    color,
    productId: productId,
    "@type": "product",
    description: description,
    mpn: mpn,
    "@context": "https://schema.org/",
    url: canonicalPath,
    name: title,
    sku: sku,
    brand: {
      "@type": "Brand",
      name: brandName,
      logo,
    },
  };

  if (reviewSummary && reviewSummary.numberOfReviews > 0) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      reviewCount: reviewSummary.numberOfReviews,
      ratingValue: reviewSummary.averageRating,
    };
  }

  return structuredData;
};
